/**
 * เปิดใบสั่งผลิตใหม่ — `POST /v1/production-orders`
 *
 * ฟอร์มนี้มีห้าช่อง และ **ไม่มีช่องที่หก** เพราะ contract ระบุว่าเซิร์ฟเวอร์เป็น
 * เจ้าของสามฟิลด์ที่ผู้ใช้อาจคาดว่าจะได้กรอก:
 *
 * - **`order_no`** เซิร์ฟเวอร์เป็นผู้กำหนด — เลขที่ client เลือกเองคือสอง client
 *   แข่งกันชน unique กันเอง
 * - **`status`** สร้างที่ `DRAFT` เสมอ การให้ client ส่งสถานะมาเองทำให้ state
 *   machine ข้อ 14 ไร้ความหมาย (contract: ส่งมาตอบ `400`)
 * - **`source_type`** บังคับเป็น `MANUAL` ใน Phase 1 ส่วน MPS/MRP/KANBAN สงวนไว้ให้
 *   source adapter ในอนาคต (D-034) การมีตัวเลือกให้ผู้ใช้เลือกคือการปล่อยให้คน
 *   อ้างได้ว่าใบนี้มาจากการวางแผน ทั้งที่พิมพ์ขึ้นมาเอง
 *
 * ทั้งสามไม่ได้ถูก "ซ่อน" — หน้าจอพูดถึงมันตรงจุดที่ผู้ใช้จะมองหาช่องกรอก เพื่อไม่ให้
 * ต้องเดาว่าช่องหายไปไหน
 *
 * ⚠️ `planned_quantity` เดินทางเป็น **string** ตลอด ไม่ผ่าน `Number` แม้แต่ตอน
 * ตรวจค่า — `NUMERIC(18,4)` ที่ผ่าน IEEE-754 double คือจำนวนที่เพี้ยนไปเงียบ ๆ
 */

import { useState, type FormEvent } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { ProductionOrderFailureNotice } from './ProductionOrderFailureNotice';
import type { ProductionOrderCreatePrefill } from './productionOrderView';
import { StatusBadge } from '../StatusBadge';
import { InfoHint } from '../components/InfoHint';
import { isPositiveDecimal } from '../../lib/bom/types';
import {
  createProductionOrder,
  type ProductionOrderFailure,
} from '../../lib/productionOrders/client';
import { isEndAfterStart, localInputToIso } from '../../lib/productionOrders/dates';
import { MANUAL_SOURCE_TYPE, type ProductionOrder } from '../../lib/productionOrders/types';

export interface ProductionOrderCreateViewProps {
  readonly apiBaseUrl: string;
  readonly fetchImpl?: typeof fetch;
  /** โรงงานที่ `plant_scope` ของผู้ใช้พามาถึงจริง (D-004) */
  readonly plants: readonly string[];
  readonly listHref: string;
  /**
   * ค่าเติมล่วงหน้าจากลิงก์ "สร้างใบสั่งผลิต" ของหน้าแผนการผลิต (D-127 ข้อ 4)
   * ใช้เป็น**ค่าเริ่มต้น**ของ state เท่านั้น — แก้ได้เหมือนกรอกเอง และไม่ส่งฟอร์ม
   * อัตโนมัติ
   */
  readonly prefill?: ProductionOrderCreatePrefill;
  /** ส่ง record ที่เซิร์ฟเวอร์ยืนยันแล้วกลับให้หน้าจอแม่พาไปที่ใบที่เพิ่งเปิด */
  readonly onCreated: (order: ProductionOrder) => void;
}

type FormState =
  | { readonly status: 'idle' }
  | { readonly status: 'invalid'; readonly problems: readonly string[] }
  | { readonly status: 'saving' }
  | { readonly status: 'failed'; readonly failure: ProductionOrderFailure };

export function ProductionOrderCreateView({
  apiBaseUrl,
  fetchImpl,
  plants,
  listHref,
  prefill,
  onCreated,
}: ProductionOrderCreateViewProps) {
  const { pathname } = useLocation();
  // ค่าเติมใช้เป็น **ค่าเริ่มต้นของ state ครั้งเดียวตอน mount** เท่านั้น — เหมือน
  // ผู้ใช้พิมพ์เอง แก้ต่อได้ตามปกติ และไม่มีการ submit ให้โดยอัตโนมัติ
  const [plantCode, setPlantCode] = useState(prefill?.plantCode || plants[0] || '');
  const [productCode, setProductCode] = useState(prefill?.productCode ?? '');
  const [quantity, setQuantity] = useState(prefill?.quantity ?? '');
  const [startLocal, setStartLocal] = useState(prefill?.startLocal ?? '');
  const [endLocal, setEndLocal] = useState(prefill?.endLocal ?? '');
  const [state, setState] = useState<FormState>({ status: 'idle' });

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (state.status === 'saving') {
      return;
    }

    /**
     * ตรวจที่หน้าจอเพื่อไม่ให้ผู้ใช้รอคำตอบที่รู้ล่วงหน้าแล้วว่าเป็นการปฏิเสธ —
     * **ไม่ใช่การแทนที่ด่านของเซิร์ฟเวอร์** ซึ่งตรวจซ้ำทุกข้อทุกคำขอเสมอ
     */
    const problems: string[] = [];
    const plant = plantCode.trim();
    const product = productCode.trim();
    // ⚠️ ไม่ trim ค่า NUMERIC: สิ่งที่พิมพ์ต้องถึงเซิร์ฟเวอร์เหมือนเดิมทุกตัวอักษร
    const startIso = localInputToIso(startLocal);
    const endIso = localInputToIso(endLocal);

    if (plant === '') {
      problems.push('ต้องเลือกโรงงาน — ใบสั่งผลิตเป็นของโรงงานใดโรงงานหนึ่งเสมอ (D-004)');
    }
    if (product === '') {
      problems.push('ต้องระบุรหัสสินค้าที่จะผลิต');
    }
    if (!isPositiveDecimal(quantity)) {
      problems.push('จำนวนที่วางแผนต้องเป็นตัวเลขมากกว่า 0');
    }
    if (startIso === null) {
      problems.push('ต้องระบุวันและเวลาที่เริ่มตามแผน');
    }
    if (endIso === null) {
      problems.push('ต้องระบุวันและเวลาที่สิ้นสุดตามแผน');
    }
    if (startIso !== null && endIso !== null && !isEndAfterStart(startIso, endIso)) {
      problems.push('วันที่สิ้นสุดตามแผนต้องอยู่หลังวันที่เริ่มตามแผน');
    }

    if (problems.length > 0 || startIso === null || endIso === null) {
      setState({ status: 'invalid', problems });
      return;
    }

    setState({ status: 'saving' });
    const result = await createProductionOrder(
      {
        plant_code: plant,
        product_code: product,
        planned_quantity: quantity,
        planned_start_date: startIso,
        planned_end_date: endIso,
      },
      { apiBaseUrl, fetchImpl },
    );
    if (result.status === 'cancelled') {
      return;
    }
    if (result.status === 'ready') {
      onCreated(result.order);
      return;
    }
    setState({ status: 'failed', failure: result });
  };

  return (
    <div className="cap-page" data-testid="production-order-create">
      <div className="cap-page__head">
        <div>
          <h1 className="cap-page__title">
            เปิดใบสั่งผลิตใหม่{' '}
            <InfoHint
              label="เกี่ยวกับหน้านี้"
              text={
                <>
                  ใบที่เปิดใหม่เป็น <strong>ร่าง (DRAFT)</strong> เสมอ
                  ยังไม่ปรากฏบนหน้างานและยังไม่ผูกกับสูตรใด — การบังคับสูตรอยู่ที่
                  ขั้นตอนสั่งปล่อย ไม่ใช่ตอนนี้
                </>
              }
            />
          </h1>
        </div>
        <div className="cap-page__actions">
          <Link
            className="cap-button cap-button--ghost"
            data-testid="production-order-create-cancel"
            to={{ pathname, search: listHref }}
          >
            กลับไปหน้ารายการ
          </Link>
        </div>
      </div>

      <form className="cap-card cap-card--form" onSubmit={(event) => void submit(event)}>
        <div className="cap-card__head">
          <h2 className="cap-card__title">
            ข้อมูลใบสั่งผลิต{' '}
            <InfoHint
              label="เกี่ยวกับฟอร์มนี้"
              text="ห้าช่องนี้คือทั้งหมดที่ API รับ — เลขที่ใบ สถานะ และที่มาของใบเป็นของเซิร์ฟเวอร์"
            />
          </h2>
        </div>

        <div className="cap-form__grid">
          <label className="cap-field">
            <span className="cap-field__label">
              โรงงาน (plant_code){' '}
              <InfoHint
                label="เกี่ยวกับรหัสโรงงาน"
                text="เลือกได้เฉพาะโรงงานที่สิทธิ์ของคุณพามาถึง (D-004) — โรงงานนอกขอบเขตเซิร์ฟเวอร์ตอบ 403"
              />
            </span>
            <select
              className="cap-input"
              name="plant_code"
              data-testid="production-order-create-plant"
              value={plantCode}
              onChange={(event) => {
                setPlantCode(event.target.value);
              }}
            >
              {plants.length === 0 ? <option value="">— ไม่มีโรงงานที่คุณเห็น —</option> : null}
              {plants.map((code) => (
                <option key={code} value={code}>
                  โรงงาน {code}
                </option>
              ))}
            </select>
          </label>

          <label className="cap-field">
            <span className="cap-field__label">
              รหัสสินค้า (product_code){' '}
              <InfoHint
                label="เกี่ยวกับรหัสสินค้า"
                text="ต้องเป็นสินค้าที่ ACTIVE และมีข้อมูลระดับโรงงานของโรงงานที่เลือก — เซิร์ฟเวอร์เป็นผู้ตรวจ"
              />
            </span>
            <input
              className="cap-input"
              type="text"
              name="product_code"
              data-testid="production-order-create-product"
              value={productCode}
              onChange={(event) => {
                setProductCode(event.target.value);
              }}
              placeholder="เช่น RM-STEEL-8MM"
            />
          </label>

          <label className="cap-field">
            <span className="cap-field__label">
              จำนวนที่วางแผน (planned_quantity){' '}
              <InfoHint
                label="เกี่ยวกับจำนวนที่วางแผน"
                text="มากกว่า 0 — ส่งเป็นข้อความตามที่พิมพ์ ไม่ปัดเศษและไม่แปลงเป็นตัวเลขระหว่างทาง"
              />
            </span>
            <input
              className="cap-input"
              type="text"
              inputMode="decimal"
              name="planned_quantity"
              data-testid="production-order-create-quantity"
              value={quantity}
              onChange={(event) => {
                setQuantity(event.target.value);
              }}
              placeholder="เช่น 250.0000"
            />
          </label>

          <label className="cap-field">
            <span className="cap-field__label">
              เริ่มตามแผน (planned_start_date){' '}
              <InfoHint
                label="เกี่ยวกับเวลาเริ่มตามแผน"
                text="ส่งเป็นเวลาตามนาฬิกาของคุณพร้อมโซนเวลาจริง ไม่ใช่เวลาที่ถูกแปลงทิ้งโซนไป"
              />
            </span>
            <input
              className="cap-input"
              type="datetime-local"
              name="planned_start_date"
              data-testid="production-order-create-start"
              value={startLocal}
              onChange={(event) => {
                setStartLocal(event.target.value);
              }}
            />
          </label>

          <label className="cap-field">
            <span className="cap-field__label">
              สิ้นสุดตามแผน (planned_end_date){' '}
              <InfoHint label="เกี่ยวกับเวลาสิ้นสุดตามแผน" text="ต้องอยู่หลังวันที่เริ่มตามแผน" />
            </span>
            <input
              className="cap-input"
              type="datetime-local"
              name="planned_end_date"
              data-testid="production-order-create-end"
              value={endLocal}
              onChange={(event) => {
                setEndLocal(event.target.value);
              }}
            />
          </label>
        </div>

        <div className="cap-notice" data-testid="production-order-server-owned" role="note">
          <StatusBadge tone="info">เซิร์ฟเวอร์เป็นเจ้าของ</StatusBadge>
          <p className="cap-notice__text">
            สามอย่างที่ไม่มีช่องให้กรอกโดยตั้งใจ{' '}
            <InfoHint
              label="เกี่ยวกับฟิลด์ที่เซิร์ฟเวอร์เป็นเจ้าของ"
              text={
                <>
                  <strong>เลขที่ใบสั่งผลิต (order_no)</strong> เซิร์ฟเวอร์เป็นผู้ออกให้
                  ตอนบันทึก — เลขที่หน้าจอเลือกเองคือสองเครื่องแข่งกันใช้เลขเดียวกัน{' '}
                  <strong>สถานะ (status)</strong> เริ่มที่ DRAFT เสมอ และเปลี่ยนได้ผ่าน
                  คำสั่งของมันเท่านั้น <strong>ที่มาของใบ (source_type)</strong> ถูกบังคับ
                  เป็น <code className="cap-table__code">{MANUAL_SOURCE_TYPE}</code> ใน
                  Phase 1 ส่วน MPS/MRP สงวนไว้ให้ตัวเชื่อมการวางแผนในอนาคต (D-034)
                  หน้าจอนี้จึงไม่ส่งทั้งสามค่าไปกับคำขอเลย
                </>
              }
            />
          </p>
        </div>

        {state.status === 'invalid' ? (
          <div className="cap-notice" data-testid="production-order-create-invalid" role="alert">
            <StatusBadge tone="warning">ยังส่งไม่ได้</StatusBadge>
            <h3 className="cap-notice__title">ตรวจข้อมูลในฟอร์มก่อน</h3>
            <ul>
              {state.problems.map((problem) => (
                <li className="cap-notice__text" key={problem}>
                  {problem}
                </li>
              ))}
            </ul>
            <p className="cap-notice__note">
              ยังไม่มีคำขอถูกส่งออกไป — การตรวจที่หน้าจอเป็นเพียงการไม่ให้รอคำตอบที่รู้อยู่แล้ว
              เซิร์ฟเวอร์ยังตรวจทุกข้อซ้ำเองเสมอ
            </p>
          </div>
        ) : null}

        {state.status === 'failed' ? (
          <ProductionOrderFailureNotice
            failure={state.failure}
            context="create"
            plantCode={plantCode}
          />
        ) : null}

        <div className="cap-form__actions">
          <button
            type="submit"
            className="cap-button cap-button--primary"
            data-testid="production-order-create-submit"
            disabled={state.status === 'saving'}
          >
            {state.status === 'saving' ? 'กำลังบันทึก…' : 'บันทึกเป็นร่าง'}
          </button>
        </div>
      </form>
    </div>
  );
}
